import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import productService from "../../api/services/productService";
import { useUser } from "../../hooks/useUser";
// ✅ ADD ONLY
import useMapLocation from "../../hooks/useMapLocation";
import { useMyVendor, useVendorById } from "../../hooks/useVendor";
import { log } from "../../utils/logger";
import SafeImage from "../../components/common/SafeImage";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { data: user } = useUser();
  const query = useQuery();
  const vendorId = query.get("vendor_id");
  const [statusUpdating, setStatusUpdating] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({});
  const perPage = 20;
  const searchInputRef = useRef(null);

  // Show Add product option
  const [isShowAddProduct, setIsShowAddProduct] = useState(true);

  const noProductsToastShown = useRef(false);

  const fetchProducts = useCallback(
    async (searchTerm = currentSearchQuery) => {
      try {
        setLoading(true);
        let result;
        if (vendorId && (user?.role === "admin" || user?.role === "employee")) {
          result = await productService.getProductsByVendorId(
            vendorId,
            page,
            perPage,
            searchTerm,
            statusFilter,
          );
        } else {
          result = await productService.getVendorProducts(
            page,
            perPage,
            searchTerm,
            statusFilter,
          );
        }

        if (result.success) {
          const productsData = Array.isArray(result.data) ? result.data : [];
          setProducts(productsData);
          setMeta(result.meta || {});

          if (productsData.length === 0 && !noProductsToastShown.current) {
            toast.error("No products found or network error!");
            noProductsToastShown.current = true;
          }
        } else {
          setProducts([]);
          setMeta({});
          toast.error("No products found or network error!");
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error(
          error.response?.data?.message ||
            "Network error! Failed to fetch products",
        );
        setProducts([]);
        setMeta({});
      } finally {
        setLoading(false);
      }
    },
    [vendorId, user?.role, page, perPage, currentSearchQuery, statusFilter],
  );
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line
  }, [vendorId, page, statusFilter, currentSearchQuery]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (meta.last_page || 1)) {
      setPage(newPage);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearch = () => {
    setCurrentSearchQuery(searchQuery);
    setPage(1); // Reset to first page when searching
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1); // Reset to first page when filtering
  };

  const handleDelete = async (product) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      let result;
      if (product.vendor_product_id) {
        // Delete vendor product
        result = await productService.deleteVendorProduct(
          product.vendor_product_id,
          user?.role,
        );
      } else {
        // Delete main product
        result = await productService.deleteProduct(
          product.product_id,
          user?.role,
        );
      }
      if (result.success) {
        toast.success("Product deleted successfully");
        fetchProducts(); // Refresh the list
      } else {
        // Check for foreign key constraint error in result.message
        if (
          result.message &&
          result.message.includes("SQLSTATE[23000]") &&
          result.message.includes("foreign key constraint fails")
        ) {
          toast.error(
            "Cannot delete this product because it has stock records. Please remove all related stocks before deleting the product.",
          );
        } else {
          toast.error(result.message || "Failed to delete product");
        }
      }
    }
  };

  const handleStatusChange = async (product, newStatus) => {
    setStatusUpdating((prev) => ({ ...prev, [product.product_id]: true }));
    try {
      let result;
      if (user?.role === "admin" || user?.role === "employee") {
        result = await productService.updateProductStatus(
          product.product_id,
          newStatus,
          user?.role,
        );
      } else {
        result = await productService.updateProduct(
          product.product_id,
          { status: newStatus },
          user?.role,
        );
      }
      if (result.success) {
        setProducts((prev) =>
          prev.map((p) =>
            p.product_id === product.product_id
              ? { ...p, status: newStatus }
              : p,
          ),
        );
        toast.success("Status updated!");
      } else {
        toast.error(result.message || "Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setStatusUpdating((prev) => ({ ...prev, [product.product_id]: false }));
    }
  };

  // ✅ ADD ONLY: Vendor currency & symbol for Base Price display
  const vendorKey =
    user?.role === "vendor"
      ? "me"
      : vendorId
        ? `vendor_${vendorId}`
        : "unknown";

  // ✅ IMPORTANT: default empty
  const [vendorCurrency, setVendorCurrency] = useState(
    sessionStorage.getItem(`vendor_currency_${vendorKey}`) || "",
  );
  const [vendorCurrencySymbol, setVendorCurrencySymbol] = useState(
    sessionStorage.getItem(`vendor_currency_symbol_${vendorKey}`) || "",
  );

  // ✅ null / "null" text varama empty return pannum
  const cleanCurrency = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).trim();
    if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "undefined")
      return "";
    return s;
  };

  // ✅ currency code -> symbol (fail na empty)
  const currencyToSymbol = (code) => {
    const c = cleanCurrency(code);
    if (!c) return "";
    try {
      const s = (0).toLocaleString("en", {
        style: "currency",
        currency: c.toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      const sym = (s || "").replace(/[0-9\s.,]/g, "").trim();
      return cleanCurrency(sym);
    } catch {
      return "";
    }
  };

  // ✅ amount format: symbol
  const formatMoney = (symbol, amount) => {
    const num = parseFloat(amount.replace(/,/g, ""));
    const val = Number.isFinite(num) ? num : 0;

    // (Products list base price usually no decimals, but you can change if needed)
    const out = String(val);

    const sym = cleanCurrency(symbol);
    return sym ? `${sym} ${out}` : `${out}`;
  };

  const COUNTRY_TO_CURRENCY = {
    "Sri Lanka": "LKR",
    "sri lanka": "LKR",
    Srilanka: "LKR",
    srilanka: "LKR",
    India: "INR",
    india: "INR",
    "United Kingdom": "GBP",
    uk: "GBP",
    USA: "USD",
    "United States": "USD",
    Canada: "CAD",
    Australia: "AUD",
    Germany: "EUR",
    France: "EUR",
    Italy: "EUR",
    Spain: "EUR",
    Japan: "JPY",
    China: "CNY",
    UAE: "AED",
    Qatar: "QAR",
    "Saudi Arabia": "SAR",
    Singapore: "SGD",
    Malaysia: "MYR",
    Thailand: "THB",
    Indonesia: "IDR",
    Philippines: "PHP",
    Pakistan: "PKR",
    Bangladesh: "BDT",
    Nepal: "NPR",
  };

  const myVendorHook = useMyVendor(); // vendor self
  const vendorByIdHook = useVendorById(vendorId); // admin/employee view vendor products

  const vendorOrigin =
    (user?.role === "vendor"
      ? myVendorHook?.data?.data?.origin ||
        myVendorHook?.data?.data?.country ||
        myVendorHook?.data?.data?.location
      : vendorId
        ? vendorByIdHook?.data?.data?.origin ||
          vendorByIdHook?.data?.data?.country ||
          vendorByIdHook?.data?.data?.location
        : "") ||
    sessionStorage.getItem("country") ||
    "";

  const { fetchCurrencyByCountryName } = useMapLocation();

  // when vendorKey changes, load cached
  useEffect(() => {
    const cachedCur =
      sessionStorage.getItem(`vendor_currency_${vendorKey}`) || "";
    const cachedSym =
      sessionStorage.getItem(`vendor_currency_symbol_${vendorKey}`) || "";

    setVendorCurrency(cachedCur);
    setVendorCurrencySymbol(cachedSym);
  }, [vendorKey]);

  // direct currency from backend if exists
  useEffect(() => {
    const directCur =
      (user?.role === "vendor"
        ? myVendorHook?.data?.data?.currency_code ||
          myVendorHook?.data?.data?.currency
        : vendorId
          ? vendorByIdHook?.data?.data?.currency_code ||
            vendorByIdHook?.data?.data?.currency
          : null) || null;

    if (directCur) {
      const finalCur = String(directCur).toUpperCase();
      const finalSym = currencyToSymbol(finalCur);

      setVendorCurrency(finalCur);
      setVendorCurrencySymbol(finalSym); // ✅ symbol empty possible

      sessionStorage.setItem(`vendor_currency_${vendorKey}`, finalCur);
      sessionStorage.setItem(`vendor_currency_symbol_${vendorKey}`, finalSym);
    } else {
      // ✅ if backend currency not exists -> keep empty (amount only)
      setVendorCurrency((prev) => prev);
      setVendorCurrencySymbol((prev) => prev);
    }
  }, [
    user?.role,
    vendorId,
    vendorKey,
    myVendorHook?.data,
    vendorByIdHook?.data,
  ]);

  // resolve by origin
  useEffect(() => {
    let cancelled = false;

    const resolveVendorCurrency = async () => {
      if (!vendorOrigin) return;

      try {
        let cur = null;

        if (fetchCurrencyByCountryName) {
          cur = await fetchCurrencyByCountryName(vendorOrigin);
        }

        if (!cur) {
          const key = (vendorOrigin || "").trim();
          cur =
            COUNTRY_TO_CURRENCY[key] || COUNTRY_TO_CURRENCY[key.toLowerCase()];
        }

        if (cancelled) return;

        const finalCur = (cur || "").toUpperCase();
        const finalSym = currencyToSymbol(finalCur);

        // ✅ Only set if we really resolved something; else keep empty
        setVendorCurrency(finalCur);
        setVendorCurrencySymbol(finalSym);

        sessionStorage.setItem(`vendor_currency_${vendorKey}`, finalCur);
        sessionStorage.setItem(`vendor_currency_symbol_${vendorKey}`, finalSym);
      } catch (e) {
        // keep old
      }
    };

    resolveVendorCurrency();
    return () => (cancelled = true);
  }, [vendorOrigin, vendorKey, fetchCurrencyByCountryName]);
  // ✅ ADD ONLY END

  const isAdminOrEmployee = user?.role === "admin" || user?.role === "employee";

  // Group products by product_id so each product appears only once
  const groupedProducts = Object.values(
    products.reduce((acc, product) => {
      if (!acc[product.product_id]) {
        acc[product.product_id] = product;
      }
      return acc;
    }, {}),
  );

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="title-header option-title d-sm-flex d-block justify-content-between align-items-center">
        <h2>Products List</h2>
        <div className="d-flex align-items-center">
          <input
            ref={searchInputRef}
            type="text"
            className="form-control form-control-sm me-2"
            style={{ width: 220 }}
            placeholder="Search by product name..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <button
            className="btn btn-primary btn-sm me-2"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? (
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
            ) : (
              <i className="fas fa-search me-1"></i>
            )}
            Search
          </button>
          <ul className="mb-0">
            <li>
              <Link
                className="btn btn-solid"
                to={
                  vendorId &&
                  (user?.role === "admin" || user?.role === "employee")
                    ? `/productsaddorupdate?vendor_id=${vendorId}`
                    : "/productsaddorupdate"
                }
              >
                Add Product
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div>
        <div
          className="table-responsive"
          style={{ maxWidth: "100%", overflowX: "auto" }}
        >
          <table
            className="table all-package theme-table table-product"
            id="table_id"
            style={{ width: "100%", marginBottom: 0 }}
          >
            <thead>
              <tr>
                <th style={{ width: "10%", padding: "6px 4px" }}>Product ID</th>
                <th style={{ width: "10%", padding: "6px 4px" }}>Image</th>
                <th style={{ width: "20%", padding: "6px 4px" }}>
                  Product Name
                </th>
                <th style={{ width: "15%", padding: "6px 4px" }}>Category</th>
                <th style={{ width: "10%", padding: "6px 4px" }}>Qty</th>
                <th style={{ width: "10%", padding: "6px 4px" }}>Base Price</th>
                {/*<th style={{ width: '10%', padding: '6px 4px' }}>Status</th>*/}
                <th style={{ width: "15%", padding: "6px 4px" }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {groupedProducts.map((product) => (
                <tr key={product.product_id}>
                  <td>{product.product_id}</td>
                  <td>
                    <div
                      className="table-image"
                      style={{ width: "40px", height: "40px" }}
                    >
                      <SafeImage
                        src={(() => {
                          try {
                            return JSON.parse(product.product_image)?.[0];
                          } catch (e) {
                            return product.product_image;
                          }
                        })()}
                        className="img-fluid"
                        alt={product.product_name}
                        style={{
                          objectFit: "cover",
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    </div>
                  </td>

                  <td
                    style={{
                      maxWidth: "0",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {product.product_name}
                  </td>

                  <td
                    style={{
                      maxWidth: "0",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {product.category}
                  </td>

                  <td>{product.totalQuantity || 0}</td>

                  <td className="td-price">
                    {formatMoney(vendorCurrencySymbol, product.price)}
                  </td>

                  <td style={{ padding: "4px" }}>
                    <ul
                      className="list-inline mb-0 d-flex align-items-center"
                      style={{ gap: "4px" }}
                    >
                      <li className="list-inline-item">
                        <Link
                          to={`/vendor-product/${product.vendor_product_id}`}
                          className="btn btn-sm btn-light p-1"
                          style={{ lineHeight: 1, minWidth: 0 }}
                        >
                          <i
                            className="ri-eye-line"
                            style={{
                              cursor: "pointer",
                              fontSize: "16px",
                            }}
                          ></i>
                        </Link>
                      </li>

                      {/* Product edit delete update */}
                      <li className="list-inline-item">
                        {
                          <Link
                            to={
                              vendorId &&
                              (user?.role === "admin" ||
                                user?.role === "employee")
                                ? `/productsaddorupdate/${product.vendor_product_id}?vendor_id=${vendorId}&source=vendor`
                                : `/productsaddorupdate/${product.vendor_product_id}?source=vendor`
                            }
                            className="btn btn-sm btn-light p-1"
                            style={{ lineHeight: 1, minWidth: 0 }}
                          >
                            <i
                              className="ri-pencil-line"
                              style={{
                                cursor: "pointer",
                                fontSize: "16px",
                              }}
                            ></i>
                          </Link>
                        }
                      </li>
                      <li className="list-inline-item">
                        {user?.role !== "vendor" && (
                          <button
                            onClick={() => handleDelete(product)}
                            className="btn btn-sm btn-light p-1"
                            style={{
                              border: "none",
                              background: "none",
                              padding: 0,
                              lineHeight: 1,
                              minWidth: 0,
                            }}
                          >
                            <i
                              className="ri-delete-bin-line"
                              style={{
                                cursor: "pointer",
                                fontSize: "16px",
                                color: "#dc3545",
                                verticalAlign: "middle",
                              }}
                            ></i>
                          </button>
                        )}
                      </li>
                    </ul>
                  </td>
                </tr>
              ))}
              {groupedProducts.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center">
                    No products found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {meta.total > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <p className="mb-0">
            Showing {meta.from || 1} to {meta.to || products.length} of{" "}
            {meta.total} entries
          </p>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item${page === 1 ? " disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  &laquo; Prev
                </button>
              </li>
              {Array.from(
                { length: Math.max(1, meta.last_page || 1) },
                (_, i) => i + 1,
              ).map((pg) => (
                <li
                  key={pg}
                  className={`page-item${pg === page ? " active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pg)}
                  >
                    {pg}
                  </button>
                </li>
              ))}
              <li
                className={`page-item${
                  page === Math.max(1, meta.last_page || 1) ? " disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === Math.max(1, meta.last_page || 1)}
                >
                  Next &raquo;
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </>
  );
}
